/**
 * ---------------------------------------------------------------------------
 * Navigation drawer (mobile sidebar) stability fix
 * ---------------------------------------------------------------------------
 *
 * Defects this works around, all in assets/theme.js:
 *
 * 1. BLANK WHITE DRAWER
 *    Open/close are Motion One timelines, and the theme only resets the menu
 *    panels on `dialog:after-hide`, which fires when the close timeline
 *    resolves. Motion One cancels an in-flight animation when a new one starts
 *    on the same element, and a cancelled Web Animation REJECTS its `finished`
 *    promise. Closing and re-opening quickly cancels the close, so
 *    `dialog:after-hide` never fires, `reinitializeDrawer()` never runs, and the
 *    drawer re-opens as a full-screen blank white panel whose close button is
 *    inside the invisible part. The drawer is also never moved back out of
 *    <body> into <header>.
 *    -> reset the panels on OPEN, and make both transitions always settle.
 *
 * 2. CLOSE, THEN IT RE-OPENS BY ITSELF  (the stubborn one)
 *    One tap can be handled twice. The focus trap's touch handler closes the
 *    drawer on `touchend`; the `click` from that same tap then reaches the
 *    `[aria-controls]` toggle handler, which sees a closed drawer and opens it
 *    again. A plain time-based guard is unreliable because the second half of
 *    the tap can arrive well after the close. So instead: an open is only
 *    honoured if the user physically pressed the menu button AFTER the drawer
 *    was closed. Anything else is the tail of the tap that just closed it.
 *
 * 3. WHITE FLASH WHILE CLOSING
 *    The close timeline fades the menu links out over 0.15s and only then
 *    slides the panel away over 0.3s, leaving a blank full-width white panel on
 *    screen in between.
 *    -> fade the whole drawer out across the entire close.
 *
 * 4. STUCK SCROLL LOCK
 *    `<html class="lock">` is driven by a shared counter that drifts when a
 *    transition is interrupted, leaving the page unscrollable.
 *    -> clear it whenever no dialog is actually open.
 *
 * Safe to delete: removing this file and its <script> tag in layout/theme.liquid
 * restores the stock behaviour.
 */

(function () {
  'use strict';

  // Longest close timeline is ~0.45s. If the promise hasn't settled by then the
  // animation was cancelled or never ran, so we settle the state ourselves.
  var FALLBACK_MS = 900;

  var CLOSE_FADE_MS = 320;

  // Absolute escape hatch: never keep a drawer shut for longer than this, so a
  // wrong guess here can't leave the menu permanently unopenable.
  var MAX_BLOCK_MS = 3000;

  /* -------------------------------------------------------------------------
     Track real presses on whatever opens a drawer, so we can tell a deliberate
     re-open from the leftover click of the tap that just closed it.
     ---------------------------------------------------------------------- */

  var lastPressByTarget = Object.create(null);

  function recordPress(event) {
    var node = event.target;

    if (!node || typeof node.closest !== 'function') return;

    var control = node.closest('[aria-controls]');

    if (!control) return;

    var id = control.getAttribute('aria-controls');

    if (id) lastPressByTarget[id] = Date.now();
  }

  ['pointerdown', 'touchstart', 'mousedown'].forEach(function (type) {
    document.addEventListener(type, recordPress, { capture: true, passive: true });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      recordPress(event);
    }
  }, { capture: true });

  /* ---------------------------------------------------------------------- */

  function getParts(drawer) {
    var root = drawer.shadowRoot;

    return {
      content: root ? root.querySelector('[part="content"]') : null,
      closeButton: root ? root.querySelector('[part="outside-close-button"]') : null
    };
  }

  /* Writes the definitive open/closed state, overriding whatever a cancelled
     animation left behind. */
  function forceState(drawer, isOpen) {
    var parts = getParts(drawer);

    drawer.style.opacity = isOpen ? '1' : '0';
    drawer.style.visibility = isOpen ? 'visible' : 'hidden';
    drawer.style.pointerEvents = isOpen ? '' : 'none';

    if (parts.content) {
      parts.content.style.transform = isOpen ? 'translateX(0%)' : '';

      Array.prototype.forEach.call(parts.content.children, function (child) {
        child.style.opacity = isOpen ? '1' : '';
      });
    }

    if (parts.closeButton) {
      parts.closeButton.style.opacity = isOpen ? '1' : '';
    }

    drawer.classList.toggle('show-close-cursor', isOpen);
  }

  /* The scroll lock is reference counted in theme.js and drifts out of sync
     when a transition is interrupted. If nothing is open, nothing is locked. */
  function syncScrollLock() {
    if (!document.querySelector('[role="dialog"][open]')) {
      document.documentElement.classList.remove('lock');
    }
  }

  function noop() {}

  /* Resolves when the animation finishes, when it is cancelled, or after a
     timeout - but always resolves. */
  function settle(controls) {
    var finished = Promise.resolve(controls && controls.then ? controls : null).then(noop, noop);
    var fallback = new Promise(function (resolve) {
      setTimeout(resolve, FALLBACK_MS);
    });

    return Promise.race([finished, fallback]);
  }

  customElements.whenDefined('navigation-drawer').then(function () {
    var proto = customElements.get('navigation-drawer').prototype;

    if (proto._navDrawerFixApplied) return;
    proto._navDrawerFixApplied = true;

    var originalShowTransition = proto._showTransition;
    var originalHideTransition = proto._hideTransition;
    var originalShow = proto.show;
    var originalHide = proto.hide;

    /* --- 2. only re-open on a fresh, deliberate press --------------------- */

    proto.hide = function () {
      this._navDrawerClosedAt = Date.now();

      // If a stale duplicate of this drawer is also open (it can be left behind
      // in <body> by an interrupted close), close that one too.
      if (this.id) {
        Array.prototype.forEach.call(document.querySelectorAll('#' + CSS.escape(this.id)), function (twin) {
          if (twin !== this && twin.hasAttribute('open')) twin.removeAttribute('open');
        }, this);
      }

      var result = originalHide.apply(this, arguments);

      setTimeout(syncScrollLock, FALLBACK_MS + 50);

      return result;
    };

    proto.show = function () {
      var closedAt = this._navDrawerClosedAt || 0;

      if (closedAt) {
        var pressedAt = (this.id && lastPressByTarget[this.id]) || 0;
        var sinceClose = Date.now() - closedAt;

        // No deliberate press since the drawer closed -> this is the leftover
        // click of the tap that closed it, not a request to re-open.
        if (pressedAt <= closedAt && sinceClose < MAX_BLOCK_MS && !window.Shopify?.designMode) {
          return Promise.resolve();
        }
      }

      return originalShow.apply(this, arguments);
    };

    /* --- 1. transitions that always settle, panels reset on open ---------- */

    proto._runTransition = function (isOpen, start) {
      var drawer = this;
      var token = (drawer._navDrawerFixToken || 0) + 1;
      var controls = null;

      drawer._navDrawerFixToken = token;

      try {
        controls = start();
      } catch (error) {
        console.warn('[navigation-drawer-fix] transition failed', error);
      }

      return settle(controls).then(function () {
        // A newer transition started in the meantime - let that one win.
        if (drawer._navDrawerFixToken === token) {
          forceState(drawer, isOpen);
        }
      });
    };

    proto._showTransition = function (animate) {
      var drawer = this;

      // If the previous close was interrupted the panels are still hidden.
      // Reset them now instead of trusting `dialog:after-hide`.
      try {
        this.reinitializeDrawer();
      } catch (error) {
        console.warn('[navigation-drawer-fix] could not reset panels', error);
      }

      this.style.removeProperty('opacity');
      this.style.removeProperty('visibility');
      this.style.removeProperty('pointer-events');

      return this._runTransition(true, function () {
        return originalShowTransition.call(drawer, animate !== false);
      });
    };

    /* --- 3. fade the whole drawer out so it never flashes white ----------- */

    proto._hideTransition = function () {
      var drawer = this;

      return this._runTransition(false, function () {
        var controls = originalHideTransition.call(drawer);

        // Runs on top of the theme's timeline (started later, so it wins) and
        // covers the whole close instead of only the last 50ms.
        if (typeof drawer.animate === 'function') {
          try {
            drawer.animate(
              [{ opacity: 1 }, { opacity: 0 }],
              { duration: CLOSE_FADE_MS, easing: 'ease-out', fill: 'forwards' }
            );
          } catch (error) {
            /* the fade is a nicety, never let it break the close */
          }
        }

        return controls;
      });
    };
  });

  /* Last-resort cleanup: returning to a backgrounded tab (where animations are
     paused and their promises never resolve) can also leave a closed drawer
     painted over the page. */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible') return;

    Array.prototype.forEach.call(document.querySelectorAll('navigation-drawer'), function (drawer) {
      if (!drawer.hasAttribute('open')) forceState(drawer, false);
    });

    syncScrollLock();
  });
})();
